import React, { useEffect, useRef, useState } from 'react';
import * as _ from 'lodash-es';

import { Box, Grid, Paper } from '@mui/material';

import * as GC from '@mescius/spread-sheets';
import * as GCD from '@mescius/spread-sheets-designer';
import { Designer } from '@mescius/spread-sheets-designer-react';
import * as ExcelIO from '@mescius/spread-excelio';

import { Header } from '../components/Header';
import { Sidebar } from '../components/Sidebar';
import { Alert } from '../components/Alert';
import { OverlayLoading } from '../components/OverlayLoading';

import { useInit } from '../services/api/useInit';
import { useBusinessReportEditor } from '../services/store/Report';
import { useSpread } from '../services/store/Spread';
import { useMappingProperty } from '../services/store/Mapping';
import { useLoop } from '../services/store/Loop';
import { useError } from '../services/store/Error';
import { useInfoDialog } from '../contexts/InfoDialog';

import { removeItemFromConfig } from '../utils/removeCommandFromConfig';
import { useStateWithGetter } from '../utils/useStateWithGetter';
import { applyLoopBackground, applyLoopHeaderBackground } from '../utils/style';
import { setPropertyByCell } from '../utils/property';
import { translate } from '../utils/translations';

import { DISABLED_TAB, EXCEL_FONT } from '../constants';
import {
  disabledContextMenuItemForCell,
  disabledContextMenuItemForSheetTab,
} from '../constants/spreadContextMenu';

const baseConfig = _.cloneDeep(GCD.Spread.Sheets.Designer.DefaultConfig);

const initDesignerConfig = () => {
  /**
   * 不要タブの削除
   **/
  baseConfig.ribbon = (
    baseConfig.ribbon as GCD.Spread.Sheets.Designer.IRibbonPanel[]
  ).filter((tab) => {
    // ホームタブの『セル』リボン
    if (
      tab.id === 'home' &&
      !_.isUndefined(tab.buttonGroups[6].commandGroup.children)
    ) {
      tab.buttonGroups[6].commandGroup.children = (
        tab.buttonGroups[6].commandGroup
          .children as GCD.Spread.Sheets.Designer.ICommandGroup[]
      ).filter((command) => {
        if (
          (command.command === 'cellsInsert' ||
            command.command === 'cellsDelete') &&
          !_.isUndefined(command.children)
        ) {
          // 『セルの挿入』『セルの削除』『セパレート（横線）』を削除
          return _.pullAt(command.children, [0, 1]);
        }

        return command;
      });
    }
    return !DISABLED_TAB.includes(tab.id);
  });

  // ファイルタブの削除
  baseConfig.fileMenu = undefined;

  /**
   * 関数（fx）欄の削除
   **/
  if (baseConfig.sidePanels) {
    baseConfig.sidePanels = baseConfig.sidePanels.filter(
      (panel) => panel.command !== 'formulaBarPanel'
    );
  }

  /**
   * 使用できるフォントを限定する
   * ・MS ゴシック
   * ・MS 明朝
   * ・MS P ゴシック
   * ・MS P 明朝
   **/
  const fontFamilyCmd = GCD.Spread.Sheets.Designer.getCommand(
    'fontFamily'
  ) as GCD.Spread.Sheets.Designer.ICommand;
  const customCNFont = [
    { value: EXCEL_FONT.MS_GOTHIC, text: EXCEL_FONT.MS_GOTHIC },
    { value: EXCEL_FONT.MS_MINCHO, text: EXCEL_FONT.MS_MINCHO },
    { value: EXCEL_FONT.MS_P_GOTHIC, text: EXCEL_FONT.MS_P_GOTHIC },
    { value: EXCEL_FONT.MS_P_MINCHO, text: EXCEL_FONT.MS_P_MINCHO },
  ];
  fontFamilyCmd.dropdownList = customCNFont;
  fontFamilyCmd.commandName = 'customFontFamily';
  baseConfig.commandMap = {
    fontFamily: fontFamilyCmd,
  };

  // 拡大縮小印刷設定をリボンコンテナから削除
  removeItemFromConfig(
    baseConfig,
    'thumbnailClass',
    'ribbon-thumbnail-scale-to-fit'
  );

  return baseConfig;
};

const initializeSheet = (sheet: GC.Spread.Sheets.Worksheet) => {
  sheet.suspendPaint();

  const style = new GC.Spread.Sheets.Style();
  style.locked = false;

  // // NOTE: 「シートを保護」を利用するかどうかは検討中
  sheet.setDefaultStyle(style);

  // 保護されたシートの設定
  sheet.options.isProtected = true;

  // 行列のリサイズ
  sheet.options.protectionOptions.allowResizeRows = false;
  sheet.options.protectionOptions.allowResizeColumns = false;
  // 行列の削除
  sheet.options.protectionOptions.allowDeleteRows = false;
  sheet.options.protectionOptions.allowDeleteColumns = false;
  // 行列の挿入
  sheet.options.protectionOptions.allowInsertRows = false;
  sheet.options.protectionOptions.allowInsertColumns = false;
  // 行列の非表示
  sheet.options.protectionOptions.allowOutlineRows = false;
  sheet.options.protectionOptions.allowOutlineColumns = false;

  sheet.options.protectionOptions.allowDragInsertRows = false;
  sheet.options.protectionOptions.allowDragInsertColumns = false;
  sheet.options.protectionOptions.allowInsertRows = false;
  sheet.options.protectionOptions.allowInsertColumns = false;
  sheet.options.protectionOptions.allowDeleteRows = false;
  sheet.options.protectionOptions.allowDeleteColumns = false;
  sheet.options.protectionOptions.allowSelectLockedCells = true;
  sheet.options.protectionOptions.allowSelectUnlockedCells = false;
  sheet.options.protectionOptions.allowSort = false;
  sheet.options.protectionOptions.allowFilter = false;
  sheet
    .getRange(0, 0, sheet.getRowCount(), sheet.getColumnCount())
    .locked(true);

  //(2-1)数式を文字列として再設定
  for (let i = 0; i < sheet.getRowCount(); i++) {
    for (let j = 0; j < sheet.getColumnCount(); j++) {
      const formula = sheet.getFormula(i, j);
      if (formula) {
        sheet.setValue(i, j, formula);
        sheet.setFormula(i, j, null); //(3)数式を削除
      }
    }
  }

  // 改ページ設定を削除
  const rowMax = sheet.getRowCount();
  const colMax = sheet.getColumnCount();
  for (let r = 0; r < rowMax; r++) {
    if (sheet.getRowPageBreak(r)) sheet.setRowPageBreak(r, false);
  }
  for (let c = 0; c < colMax; c++) {
    if (sheet.getColumnPageBreak(c)) sheet.setColumnPageBreak(c, false);
  }

  // 拡大縮小印刷設定
  const pi = sheet.printInfo() as GC.Spread.Sheets.Print.PrintInfo;
  pi.fitPagesWide(1);
  pi.fitPagesTall(1);
  pi.zoomFactor(1);
  sheet.printInfo(pi);

  sheet.resumePaint();
};

const initializeSpread = (spread: GC.Spread.Sheets.Workbook) => {
  // 新規でシートを追加させないようにする
  spread.options.newTabVisible = false;
  // シートタブを表示させないようにする
  // spread.options.tabStripVisible = false;
  // 数式の入力を無効にする
  spread.options.allowUserEditFormula = false;
  // 数式の入力を無効にする
  spread.options.calculationMode = GC.Spread.Sheets.CalculationMode.manual;

  // 読み込み以降にも自動で計算が実行されないようにする
  spread.options.calculationMode = GC.Spread.Sheets.CalculationMode.manual;

  // シートの設定
  _.forEach(spread.sheets, (sheet) => {
    initializeSheet(sheet);
  });
};

/**
 * テンプレート編集画面
 * Excelテンプレートを編集する画面
 * テンプレートの内容をJSON形式で出力したりできる
 * @returns JSX.Element
 */
export function ReportDetail(): React.JSX.Element {
  const [spreadOpen, setSpreadOpen] = useState<boolean>(false);
  const { reportFormatFile, isInitialRequestComplete } =
    useBusinessReportEditor();
  const { mappingProperties } = useMappingProperty();
  const { loops } = useLoop();
  const { spread, setSpread } = useSpread();
  const { error, setError } = useError();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isMappingRendered, setIsMappingRendered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const infoDialog = useInfoDialog();

  const [, setFormatFileLoaded, getFormatFileLoaded] =
    useStateWithGetter(false);

  const ref = useRef<HTMLDivElement>(null);

  const designerConfig = initDesignerConfig();

  /**
   * Init処理
   */
  useInit();

  /**
   * SpreadJSのワークブックの初期化
   */
  const initWorkbook = (designer: GCD.Spread.Sheets.Designer.Designer) => {
    if (isInitialized) return;

    const spread = designer.getWorkbook() as GC.Spread.Sheets.Workbook;

    /**
     * アクティブシートの変更時
     * ・数式の入力を無効にする
     */
    spread.bind(GC.Spread.Sheets.Events.SheetTabClick, () => {
      spread.options.allowUserEditFormula = false;
      spread.options.calculationMode = GC.Spread.Sheets.CalculationMode.manual;
    });

    /**
     * シート名が変更された時のイベント
     */
    spread.bind(
      GC.Spread.Sheets.Events.SheetNameChanging,
      async (
        sender: unknown,
        args: GC.Spread.Sheets.ISheetNameChangingEventArgs
      ) => {
        if (args.newValue.length > 32) {
          args.cancel = true;
          const ok = await infoDialog({
            message: translate('ERROR.SHEET_NAME_OVER_LENGTH'),
          });
          if (ok) return;
        }
      }
    );

    const defaultOnOpenMenu = spread.contextMenu.onOpenMenu.bind(
      spread.contextMenu
    );

    /**
     * コンテキストメニューの表示制御
     */
    spread.contextMenu.onOpenMenu = (
      menuData,
      itemDataForShown,
      hitInfo: { worksheetHitInfo?: GC.Spread.Sheets.IHitTestInformation },
      workbook
    ) => {
      const result = defaultOnOpenMenu(
        menuData,
        itemDataForShown,
        hitInfo,
        workbook
      );

      if (hitInfo.worksheetHitInfo) {
        const { row, col, hitTestType } = hitInfo.worksheetHitInfo;

        if (_.isUndefined(row) || _.isUndefined(col)) return result;

        // セルのコンテキストメニューから特定のアイテムを除外する
        if (hitTestType === GC.Spread.Sheets.SheetArea.viewport) {
          for (let i = itemDataForShown.length - 1; i >= 0; i--) {
            if (
              disabledContextMenuItemForCell.has(itemDataForShown[i].name ?? '')
            ) {
              itemDataForShown.splice(i, 1);
            }
          }
        }
        return result;
      }

      // シートタブのコンテキストメニューから特定のアイテムを除外する
      for (let i = itemDataForShown.length - 1; i >= 0; i--) {
        if (
          disabledContextMenuItemForSheetTab.has(itemDataForShown[i].name ?? '')
        ) {
          itemDataForShown.splice(i, 1);
        }
      }

      return result;
    };

    setSpread(spread);
    setIsInitialized(true);
  };

  /**
   * 帳票フォーマット読み込み時の処理
   */
  useEffect(() => {
    if (
      _.isNull(reportFormatFile) ||
      _.isNull(spread) ||
      getFormatFileLoaded()
    ) {
      return;
    }
    setFormatFileLoaded(true);

    const excelIO = new ExcelIO.IO();
    excelIO.open(
      reportFormatFile.file,
      (json: JSON) => {
        spread.fromJSON(json);

        initializeSpread(spread);

        setSpreadOpen(true);
      },
      (e: unknown) => {
        setSpreadOpen(false);
        console.log(e);
      }
    );
  }, [reportFormatFile, spread]);

  /**
   * マッピング情報がある場合はリストアされた情報を元にsheetを更新する
   */
  useEffect(() => {
    if (
      getFormatFileLoaded() &&
      spreadOpen &&
      !isMappingRendered &&
      isInitialRequestComplete
    ) {
      if (!spread) return;

      const targetSheets: number[] = [];

      // 描画の停止（描画を１つにまとめる）
      spread.suspendPaint();

      // ループの設定
      _.forEach(loops, (loop) => {
        const sheet = spread.getSheet(loop.sheetId);

        if (_.isUndefined(sheet)) return;

        const { startRowIndex, startColIndex, endRowIndex, endColIndex } =
          loop.loopRange;

        applyLoopBackground(sheet, {
          startRow: startRowIndex,
          startCol: startColIndex,
          endRow: endRowIndex,
          endCol: endColIndex,
        });

        // TODO: mappingEnableCellRangeがBFFの修正で返るようになればif文は不要
        if (loop.loopHeaderRange) {
          // 明細範囲のヘッダー行の復元
          const loopHeaderRanges = GC.Spread.Sheets.CalcEngine.formulaToRanges(
            sheet,
            loop.loopHeaderRange,
            0,
            0
          );
          const loopHeaderRange = (
            loopHeaderRanges[0] as { ranges: GC.Spread.Sheets.Range[] }
          ).ranges[0];

          applyLoopHeaderBackground(sheet, {
            startRow: loopHeaderRange.row,
            startCol: loopHeaderRange.col,
            headerEndRow: loopHeaderRange.row + loopHeaderRange.rowCount - 1,
            headerEndCol: loopHeaderRange.col + loopHeaderRange.colCount - 1,
          });
        }

        targetSheets.push(loop.sheetId);
      });

      // プロパティと紐づいたセルの設定
      _.forEach(mappingProperties, (prop) => {
        const sheet = spread.getSheet(prop.sheetId);

        if (_.isUndefined(sheet)) return;

        setPropertyByCell(sheet, prop.rowIndex, prop.colIndex, prop);

        targetSheets.push(prop.sheetId);
      });

      // 初期のマッピング情報をisDirtyの対象から除外する
      const sheetIds = new Set(targetSheets);
      _.forEach([...sheetIds], (id) => {
        spread.getSheet(id).clearPendingChanges();
      });

      // 描画の再開
      spread.resumePaint();

      setIsMappingRendered(true);
    }
  }, [
    isInitialRequestComplete,
    isMappingRendered,
    loops,
    mappingProperties,
    spread,
    spreadOpen,
  ]);

  useEffect(() => {
    if (spreadOpen && isMappingRendered) {
      setIsLoading(false);
    } else {
      setIsLoading(true);
    }
  }, [spreadOpen, isMappingRendered]);

  return (
    <>
      <Header />

      <Box
        sx={
          spreadOpen
            ? {
                height: 'calc(100vh - 88px)',
              }
            : {
                width: 0,
                height: 0,
                visibility: 'hidden',
                overflow: 'hidden',
              }
        }
      >
        <Grid container spacing={2} sx={{ height: '100%' }}>
          <Grid size="auto" sx={{ height: '100%' }}>
            <Sidebar
              initDataset={false}
              setFormatFileLoaded={setFormatFileLoaded}
            />
          </Grid>
          <Grid size="grow" sx={{ height: '100%' }}>
            <Paper sx={{ paddingRight: 2, height: '100%' }}>
              <div ref={ref} role={'Dustbin'} style={{ height: '100%' }}>
                <Designer
                  config={designerConfig}
                  designerInitialized={initWorkbook}
                  styleInfo={{ width: '100%', height: '100%' }}
                ></Designer>
              </div>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Alert
        open={!_.isNull(error)}
        handleClose={() => setError(null)}
        message={error?.message ?? ''}
        title={error?.title ?? ''}
      />

      <OverlayLoading loading={isLoading} />
    </>
  );
}
